import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RatesData, GetRatesAndRosterResponse } from '../../pages/rosterAndRates/model';

// Interface for category override data (roster adjustments - hours only)
export interface CategoryOverride {
  override?: number; // Hours override only
  overrideReason?: string;
  overrideComment?: string;
}

// Interface for rate override data
export interface RateOverride {
  override?: number; // Rate override (UPH)
  overrideReason?: string;
  overrideComment?: string;
  processPath?: string;
  functionId?: string;
  functionName?: string;
  functionType?: string;
}

// Interface for total attendance override data
export interface TotalAttendanceOverride {
  override: number | null;
  overrideReason?: string | null;
  overrideComment?: string | null;
}

// Main state interface
export interface RosterAndRatesState {
  totalAttendanceOverride: TotalAttendanceOverride | null;
  categoryOverrides: Record<string, CategoryOverride>; // Category name as key
  rateOverrides: Record<string, RateOverride>; // Function ID as key
  ratesAndRoster: GetRatesAndRosterResponse | null; // Store the complete rates and roster data
  // planId whose backend overrides currently populate the override maps. Used to
  // detect a plan switch (published-plan page version selector) so we re-seed
  // overrides from the new plan instead of keeping the previous plan's values.
  // Optional so existing mock state objects (and older persisted state) remain
  // valid; the reducer treats an absent value the same as null.
  loadedPlanId?: string | null;
  ratesDataLoading: boolean; // Loading state for rates data API call
  loading: boolean; // General loading state
  retryCount: number;
  error: string | null;
}

// Initial state
const initialState: RosterAndRatesState = {
  totalAttendanceOverride: null,
  categoryOverrides: {},
  rateOverrides: {},
  ratesAndRoster: null,
  loadedPlanId: null,
  ratesDataLoading: false,
  loading: false,
  retryCount: 0,
  error: null,
};

// Payload interfaces for actions
export interface UpdateCategoryOverridePayload {
  categoryName: string;
  field: 'override' | 'overrideReason' | 'overrideComment';
  value: number | string | undefined | null;
}

export interface UpdateRateOverridePayload {
  functionId: string;
  field: 'override' | 'overrideReason' | 'overrideComment';
  value: number | string | undefined | null;
}

export interface UpdateTotalAttendanceOverridePayload {
  field: 'override' | 'overrideReason' | 'overrideComment';
  value: number | string;
}

export interface SaveCategoryOverridePayload {
  categoryName: string;
  override: number;
  overrideReason: string;
  overrideComment: string;
}

export interface SaveTotalAttendanceOverridePayload {
  override: number;
  overrideReason: string;
  overrideComment: string;
}

// Create the slice
const rosterAndRatesSlice = createSlice({
  name: 'rosterAndRates',
  initialState,
  reducers: {
    // Update individual category override fields
    updateCategoryOverride: (state, action: PayloadAction<UpdateCategoryOverridePayload>) => {
      const { categoryName, field, value } = action.payload;
      
      if (!state.categoryOverrides[categoryName]) {
        state.categoryOverrides[categoryName] = {};
      }
      
      if (field === 'override') {
        state.categoryOverrides[categoryName].override = value as number;
      } else if (field === 'overrideReason') {
        state.categoryOverrides[categoryName].overrideReason = value as string;
      } else if (field === 'overrideComment') {
        state.categoryOverrides[categoryName].overrideComment = value as string;
      }
    },

    // Update rate override fields
    updateRateOverride: (state, action: PayloadAction<UpdateRateOverridePayload>) => {
      const { functionId, field, value } = action.payload;
      
      if (!state.rateOverrides[functionId]) {
        state.rateOverrides[functionId] = {};
      }
      
      if (field === 'override') {
        state.rateOverrides[functionId].override = value as number;
      } else if (field === 'overrideReason') {
        state.rateOverrides[functionId].overrideReason = value as string;
      } else if (field === 'overrideComment') {
        state.rateOverrides[functionId].overrideComment = value as string;
      }
    },

    // Update total attendance override fields
    updateTotalAttendanceOverride: (state, action: PayloadAction<UpdateTotalAttendanceOverridePayload>) => {
      const { field, value } = action.payload;
      
      if (!state.totalAttendanceOverride) {
        state.totalAttendanceOverride = {
          override: field === 'override' ? (value as number) : null,
          overrideReason: field === 'overrideReason' ? (value as string) : '',
          overrideComment: field === 'overrideComment' ? (value as string) : ''
        };
      } else {
        // Preserve existing values and only update the specified field
        if (field === 'override') {
          state.totalAttendanceOverride.override = value as number;
        } else if (field === 'overrideReason') {
          state.totalAttendanceOverride.overrideReason = value as string;
        } else if (field === 'overrideComment') {
          state.totalAttendanceOverride.overrideComment = value as string;
        }
      }
    },

    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    // Set error state
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Increment retry count
    incrementRatesAndRosterRetryCount: (state) => {
       state.retryCount = state.retryCount + 1;
    },

    // Reset retry count. Used when the plan being loaded changes (e.g. selecting a
    // different version on the published-plan page, which reuses the same mounted
    // component) so the new plan's polling starts fresh instead of inheriting the
    // previous plan's accumulated count and short-circuiting into "max retries".
    //
    // `error` is cleared with it. Both are single slice-wide slots describing one
    // plan's load attempt, so leaving the error behind would let a previous plan's
    // "max retries exceeded" message render as the new plan's empty state — the
    // same staleness the retryCount reset exists to prevent.
    resetRatesAndRosterRetryCount: (state) => {
       state.retryCount = 0;
       state.error = null;
    },

    // Initialize category overrides from fetched data
    initializeCategoryOverrides: (state, action: PayloadAction<Record<string, CategoryOverride>>) => {
      state.categoryOverrides = action.payload;
    },

    // Initialize total attendance override from fetched data
    initializeTotalAttendanceOverride: (state, action: PayloadAction<TotalAttendanceOverride>) => {
      state.totalAttendanceOverride = action.payload;
    },

    // Clear all overrides
    clearAllOverrides: (state) => {
      state.categoryOverrides = {};
      state.rateOverrides = {};
      state.totalAttendanceOverride = null;
    },

    // Clear specific category override
    clearCategoryOverride: (state, action: PayloadAction<string>) => {
      const categoryName = action.payload;
      delete state.categoryOverrides[categoryName];
    },

    // Set rates and roster data.
    // Accepts either the bare response (existing callers) or an object carrying the
    // planId the response was fetched for. When the planId differs from the one whose
    // overrides currently populate state (published-plan page version switch), the
    // previous plan's overrides are cleared so this plan's backend overrides re-seed
    // below instead of being blocked by the "already exists" guards.
    setRatesAndRoster: (
      state,
      action: PayloadAction<GetRatesAndRosterResponse | { response: GetRatesAndRosterResponse; planId?: string | null }>
    ) => {
      const payload = action.payload as GetRatesAndRosterResponse & {
        response?: GetRatesAndRosterResponse;
        planId?: string | null;
      };
      const response: GetRatesAndRosterResponse = payload.response ?? (action.payload as GetRatesAndRosterResponse);
      const fetchedPlanId: string | null = payload.response ? (payload.planId ?? null) : null;

      // Clear the previous plan's overrides only when we're switching to a
      // genuinely different, known plan. Use loose null checks (`!= null`) so an
      // absent/undefined loadedPlanId (e.g. preloaded test state) is treated the
      // same as null and does NOT trigger a spurious clear of seeded overrides.
      if (fetchedPlanId != null && state.loadedPlanId != null && fetchedPlanId !== state.loadedPlanId) {
        state.categoryOverrides = {};
        state.rateOverrides = {};
        state.totalAttendanceOverride = null;
      }
      if (fetchedPlanId != null) {
        state.loadedPlanId = fetchedPlanId;
      }

      state.ratesAndRoster = response;
      // Initialize overrides from the roster data only if they don't already exist
      if (response.rosterData) {
        // Initialize total attendance override if it exists in response AND no override is currently set
        if (!state.totalAttendanceOverride &&
            (response.rosterData.totalAttendance?.override || 
             response.rosterData.totalAttendance?.overrideReason ||
             response.rosterData.totalAttendance?.overrideComment)) {
          state.totalAttendanceOverride = {
            override: response.rosterData.totalAttendance.override || 0,
            overrideReason: response.rosterData.totalAttendance.overrideReason,
            overrideComment: response.rosterData.totalAttendance.overrideComment,
          };
        }
        
        // Initialize category overrides if they exist in response and don't already exist
        response.rosterData.categories?.forEach((category) => {
          if (!state.categoryOverrides[category.name] &&
              (category.override || category.overrideReason || category.overrideComment)) {
            state.categoryOverrides[category.name] = {
              override: category.override,
              overrideReason: category.overrideReason,
              overrideComment: category.overrideComment,
            };
          }
        });
      }

      // Initialize rate overrides from function rates if backend returns override data.
      // The backend may include overrideRate/overrideReason/overrideComment on each
      // function rate entry when a plan has previously-saved rate overrides.
      if (response.ratesData?.functionRates) {
        response.ratesData.functionRates.forEach((funcRate) => {
          // Backend may send optional override fields not yet in the base FunctionRate type.
          // Access via bracket notation to safely extract them without `any` casts.
          const rateRecord = funcRate as unknown as Record<string, unknown>;
          const overrideRate = rateRecord.overrideRate as number | undefined;
          const overrideReason = rateRecord.overrideReason as string | undefined;
          const overrideComment = rateRecord.overrideComment as string | undefined;
          if (!state.rateOverrides[funcRate.functionId] &&
              (overrideRate || overrideReason || overrideComment)) {
            state.rateOverrides[funcRate.functionId] = {
              override: overrideRate,
              overrideReason: overrideReason,
              overrideComment: overrideComment,
              processPath: funcRate.processPath,
              functionId: funcRate.functionId,
              functionName: funcRate.functionName,
              functionType: funcRate.functionType,
            };
          }
        });
      }
    },

    // Set rates data loading state
    setRatesDataLoading: (state, action: PayloadAction<boolean>) => {
      state.ratesDataLoading = action.payload;
    },

    // Clear rates and roster data
    clearRatesAndRoster: (state) => {
      state.ratesAndRoster = null;
    },

  },
});

// Export actions
export const {
  updateCategoryOverride,
  updateRateOverride,
  updateTotalAttendanceOverride,
  setLoading,
  setError,
  initializeCategoryOverrides,
  initializeTotalAttendanceOverride,
  clearAllOverrides,
  clearCategoryOverride,
  setRatesAndRoster,
  setRatesDataLoading,
  clearRatesAndRoster,
  incrementRatesAndRosterRetryCount,
  resetRatesAndRosterRetryCount,
} = rosterAndRatesSlice.actions;

// Export reducer
export default rosterAndRatesSlice.reducer;
